import {
  Button,
  Dialog,
  DialogContent,
  InputLabel,
  ModalProps,
  Stack,
  TextField,
  Typography,
  DialogActions,
  DialogContentText,
} from '@mui/material'
import { useFormik } from 'formik'
import isNil from 'lodash/isNil'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import { DatePicker } from '@omnea-app/fe-legacy/components/shared/form/date-picker/DatePicker'
import { EditableDropdownField } from '@omnea-app/fe-legacy/components/shared/ui/editable-fields/EditableDropdownField'
import { AssigneeAutocomplete, AssigneeSourceType } from '@omnea-app/fe-users-and-groups/assignees/AssigneeAutocomplete'
import { DialogHeader } from '@omnea-app/omui/DialogHeader'
import { useRequestComments } from '../comments/api/useRequestComments'
import { PauseRequestFormValues, PauseRequestFormValidationSchema, initialFormValues } from './formHelpers'
import { IntegrationWarningModal } from './IntegrationWarningModal'
import { PauseReasonSelect } from './PauseReasonSelect'
import { usePauseRequest, Pause } from './usePauseRequest'

interface Props {
  requestId: string
  open: boolean
  modalProps?: Partial<Omit<ModalProps, 'role'>>
  closeModal?: () => void
}

export const PauseModal = ({ open, requestId, closeModal, modalProps }: Props) => {
  const { t } = useTranslation('requests')
  const { addPauseComment } = useRequestComments({ requestId })
  const [runningIntegrationSteps, setRunningIntegrationSteps] = useState([])

  const form = useFormik<PauseRequestFormValues>({
    initialValues: initialFormValues,
    onSubmit: (values) =>
      pauseRequest({
        requestId,
        // oxlint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: bulk migration to add error
        assigneeId: values.assignee!.id,
        // oxlint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: bulk migration to add error
        reasonId: values.reasonId!,
        note: values.note,
        // oxlint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: bulk migration to add error
        reminderDate: values.reminderDate!,
      }),
    validationSchema: toFormikValidationSchema(PauseRequestFormValidationSchema),
    validateOnChange: false,
  })

  const { values, setFieldValue, handleSubmit } = form

  const handlePauseSuccess = ({ pause_reason, note }: Pause) => {
    void addPauseComment({ reason: pause_reason, note })
    closeModal?.()
  }
  const handlePauseError = (err: any) => {
    if (err.detail?.integrationSteps) {
      // Integration steps are running
      closeModal?.()
      setRunningIntegrationSteps(err.detail.integrationSteps)
    }
  }
  const { mutate: pauseRequest, isPending } = usePauseRequest({ onSuccess: handlePauseSuccess, onError: handlePauseError })

  return (
    <>
      <Dialog open={open} onClose={closeModal} fullWidth maxWidth="sm" {...modalProps}>
        <DialogHeader title={t('pause.modal-header')} onClose={closeModal} />
        <DialogContent>
          <DialogContentText sx={{ mb: 4 }}>{t('pause.modal-description')}</DialogContentText>
          <Stack spacing={5}>
            <PauseReasonSelect
              label={t('pause.reason')}
              required
              value={values.reasonId}
              onChange={(reasonId) => void setFieldValue('reasonId', reasonId)}
              error={!!form.errors.reasonId}
              helperText={form.errors.reasonId && t(form.errors.reasonId)}
            />
            <Stack>
              <AssigneeAutocomplete
                label={t('pause.assignee')}
                required
                values={values.assignee ? [values.assignee] : []}
                type={AssigneeSourceType.USERS}
                allowSingleAssigneeOnly
                onChange={([newAssignee]) => setFieldValue('assignee', newAssignee)}
                title={t('create-brand.access-owner')}
                error={form.errors.assignee ? 'empty_assignees' : undefined}
                helperText={form.errors.assignee && t(form.errors.assignee)}
              />
              <Typography variant="small-regular" color="text.muted" sx={{ mt: 1 }}>
                {t('pause.assignee-description')}
              </Typography>
            </Stack>
            <Stack>
              <InputLabel required>{t('pause.remind-on')}</InputLabel>
              <DatePicker
                fullWidth
                value={values.reminderDate}
                onChange={(newValue) => setFieldValue('reminderDate', !isNil(newValue) ? new Date(newValue) : undefined)}
                textFieldProps={{
                  error: !!form.errors.reminderDate,
                  helperText: form.errors.reminderDate && t(form.errors.reminderDate),
                }}
              />
            </Stack>
            <Stack>
              <TextField
                label={t('pause.description')}
                placeholder={t('pause.description-placeholder')}
                value={values.note}
                onChange={(e) => setFieldValue('note', e.target.value)}
                multiline
                minRows={3}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} color="secondary" variant="contained">
            {t('cancel', { ns: 'common', keyPrefix: '' })}
          </Button>
          <Button variant="contained" color="primary" onClick={() => handleSubmit()} loading={isPending}>
            {t('pause.pause-request')}
          </Button>
        </DialogActions>
      </Dialog>
      <IntegrationWarningModal
        open={runningIntegrationSteps.length > 0}
        onClose={() => setRunningIntegrationSteps([])}
        integrationSteps={runningIntegrationSteps}
      />
    </>
  )
}
